import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X, AlertTriangle } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   referenceCode?: string,
 *   products?: Array<{ id: string, name?: string, allocatedQty?: number|string }>,
 *   stubSubmit?: boolean,
 *   submitting?: boolean,
 *   submitError?: string,
 *   onSubmit?: (payload: { reason: string, lineIds: string[] }) => void,
 * }} props
 */
export function RequestRevokeModal({
  open,
  onOpenChange,
  referenceCode,
  products = [],
  stubSubmit = false,
  submitting = false,
  submitError = "",
  onSubmit,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState("");

  const productIds = useMemo(
    () => products.map((p) => String(p.id)),
    [products]
  );
  const showProductList = products.length > 0 || stubSubmit;

  useEffect(() => {
    if (!open) return;
    setReason("");
    setError("");
    setSelectedIds(products.map((p) => String(p.id)));
  }, [open, products]);

  const allSelected =
    productIds.length > 0 && productIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : [...productIds]);
    setError("");
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (showProductList && selectedIds.length === 0) {
      setError(t("validation.selectAtLeastOneProduct"));
      return;
    }

    if (stubSubmit) {
      onOpenChange(false);
      return;
    }

    if (!reason.trim()) {
      setError(t("validation.reasonRequired"));
      return;
    }
    if (reason.trim().length < 10) {
      setError(t("inventory.reasonMinLength"));
      return;
    }

    if (typeof onSubmit === "function") {
      onSubmit({ reason: reason.trim(), lineIds: selectedIds });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[560px] max-h-[85vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden overflow-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4.5 w-4.5 text-rose-500" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.requestAllocationRevoke")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close
              disabled={submitting}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          {referenceCode ? (
            <div className="text-xs per-file-hint font-semibold leading-relaxed shrink-0">
              {t("formActions.revokeRequestBeforeRef")}{" "}
              <strong className="text-foreground font-mono">{referenceCode}</strong>.{" "}
              {stubSubmit
                ? t("inventory.revokeNotImplementedHint")
                : t("formActions.revokeRequestAfterRef")}
            </div>
          ) : stubSubmit ? (
            <p className="text-xs per-file-hint font-semibold shrink-0">
              {t("inventory.revokeNotImplementedHint")}
            </p>
          ) : null}

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 min-h-0 flex-1 overflow-hidden"
          >
            {showProductList ? (
              products.length === 0 ? (
                <div className="rounded-xl border border-(--color-text-border)/60 bg-workspace/30 px-4 py-6 text-center text-xs font-semibold per-file-hint">
                  {t("inventory.noRevocableProducts")}
                </div>
              ) : (
                <div className="rounded-xl border border-(--color-text-border)/60 overflow-hidden min-h-0 flex flex-col">
                  <div className="flex items-center gap-3 px-3.5 py-2.5 bg-gray-50/80 border-b border-(--color-text-border)/50 shrink-0">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={toggleAll}
                      disabled={submitting}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-accent cursor-pointer"
                      aria-label={t("formActions.selectAll")}
                    />
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
                      {t("formActions.selectAll")}
                    </span>
                  </div>
                  <div className="overflow-y-auto max-h-[36vh] divide-y divide-(--color-text-border)/40">
                    {products.map((product) => {
                      const id = String(product.id);
                      return (
                        <label
                          key={id}
                          className="flex items-start gap-3 px-3.5 py-3 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(id)}
                            onChange={() => toggleOne(id)}
                            disabled={submitting}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-accent cursor-pointer shrink-0"
                          />
                          <span className="min-w-0">
                            <span className="block text-xs font-bold text-foreground truncate">
                              {product.name || "—"}
                            </span>
                            <span className="block text-[11px] per-file-hint font-semibold mt-0.5">
                              {t("inventory.allocatedQty")}:{" "}
                              <span className="font-mono text-foreground">
                                {product.allocatedQty ?? "—"}
                              </span>
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )
            ) : null}

            {!stubSubmit ? (
              <div className="space-y-1.5 shrink-0">
                <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                  {t("inventory.reasonForRevocation")}{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder={t("formActions.revokeReasonPlaceholder")}
                  disabled={submitting}
                  className={cn(
                    "w-full h-24 p-3 rounded-xl border bg-white text-xs font-semibold focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 resize-none",
                    error ? "border-rose-500" : "border-(--color-text-border)"
                  )}
                />
              </div>
            ) : null}

            {error || submitError ? (
              <p className="text-[11px] text-rose-500 font-bold shrink-0">
                ⚠️ {error || submitError}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
                className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={
                  submitting || (showProductList && products.length === 0)
                }
                className="h-10 px-5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(239,68,68,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? t("inventory.saving")
                  : stubSubmit
                    ? t("formActions.close")
                    : t("formActions.submitRequest")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
